// ============================================================================
// ClientReportPage — route /me/report (guarded). The client's OWN full progress
// report (GET /api/client/me/report), the same aggregation the trainer sees:
// body trends + before/after deltas, body ratios, strength PRs + tonnage,
// adherence, and goal progress. Period selectable (all / 30 / 90 / 365 days).
// Mobile-first, Pulse, EN + 繁中, reduced-motion aware. Sections render only
// when they carry data (optional-first — no fake zeros).
// ============================================================================
import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Badge, Button, Card, CardHeader, EmptyState, StatTile } from "../../components";
import { TrendChart } from "../../pages/client-detail/TrendChart";
import { useClientReport } from "../hooks";
import { fmtNum, fmtSigned } from "./portalLib";
import { CLIENT_HOME_PATH } from "../routes";
import type { SeriesPoint } from "../../api";

type Period = "all" | "month" | "quarter" | "year";

interface Delta {
  before: number;
  after: number;
  change: number;
}

export function ClientReportPage() {
  const { t } = useTranslation("clientPortal");
  const reduce = useReducedMotion();
  const [period, setPeriod] = useState<Period>("all");
  const report = useClientReport(period);

  const fade = (delay = 0) => ({
    initial: reduce ? false : { opacity: 0, y: 14 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.4, delay: reduce ? 0 : delay, ease: [0.22, 0.61, 0.36, 1] as const },
  });

  const periods: Period[] = ["all", "month", "quarter", "year"];

  return (
    <div className="space-y-6">
      <Link
        to={CLIENT_HOME_PATH}
        className="inline-flex items-center gap-1.5 text-sm font-bold text-body-soft transition-colors hover:text-coral"
      >
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden>
          <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        {t("report.back")}
      </Link>

      <motion.div {...fade(0)}>
        <h1 className="font-display text-2xl font-extrabold tracking-tightest text-body">
          {t("nav.report")}
        </h1>
        {/* Period selector */}
        <div className="mt-3 inline-flex flex-wrap gap-1 rounded-pill bg-surface-sunken p-1 ring-1 ring-line">
          {periods.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPeriod(p)}
              aria-pressed={period === p}
              className={
                "rounded-pill px-3 py-1 font-display text-xs font-bold transition-colors " +
                (period === p
                  ? "bg-grad-coral text-white shadow-volt"
                  : "text-body-soft hover:text-body")
              }
            >
              {t(`report.period.${p}`)}
            </button>
          ))}
        </div>
      </motion.div>

      {report.isLoading && <ReportSkeleton />}

      {report.isError && (
        <EmptyState
          title={t("common.loadError")}
          description={t("common.loadErrorBlurb")}
          action={
            <Button size="sm" onClick={() => void report.refetch()}>
              {t("common.retry")}
            </Button>
          }
        />
      )}

      {report.data && <ReportBody data={report.data} fade={fade} />}
    </div>
  );
}

function ReportBody({
  data,
  fade,
}: {
  data: Record<string, unknown>;
  fade: (d?: number) => Record<string, unknown>;
}) {
  const { t } = useTranslation("clientReport");
  const { t: tp } = useTranslation("clientPortal");

  const body = (data.body ?? {}) as {
    ok?: boolean;
    series?: Record<string, SeriesPoint[]>;
    deltas?: Record<string, Delta>;
  };
  const adherence = (data.adherence ?? {}) as {
    ok?: boolean;
    completed?: number;
    planned?: number;
    completion_pct?: number | null;
  };
  const strength = (data.strength ?? {}) as {
    ok?: boolean;
    total_tonnage?: number | null;
    prs?: Array<{ exercise_name: string | null; best_1rm: number }>;
  };
  const goals = (data.goals ?? {}) as {
    ok?: boolean;
    items?: Array<{
      kind: string | null;
      metric_key: string;
      current: number | null;
      target: number | null;
      progress_pct: number | null;
    }>;
  };

  const anyData = body.ok || adherence.ok || strength.ok || goals.ok;
  if (!anyData) {
    return (
      <EmptyState title={t("empty.title")} description={t("empty.blurb")} />
    );
  }

  const weightDelta = body.deltas?.weight;
  const bfDelta = body.deltas?.body_fat;

  return (
    <div className="space-y-6">
      {/* Overview KPIs from deltas + adherence */}
      <motion.div {...(fade(0.04) as object)} className="grid grid-cols-2 gap-3">
        {weightDelta && (
          <StatTile
            label={t("overview.weightChange")}
            value={Math.abs(weightDelta.change)}
            decimals={1}
            prefix={weightDelta.change > 0 ? "+" : weightDelta.change < 0 ? "−" : ""}
            suffix=" kg"
            accent="coral"
          />
        )}
        {adherence.ok && adherence.completion_pct != null && (
          <StatTile
            label={t("overview.completion")}
            value={adherence.completion_pct}
            decimals={0}
            suffix="%"
            accent="lime"
          />
        )}
        {adherence.ok && (
          <StatTile
            label={t("overview.sessionsDone")}
            value={adherence.completed ?? 0}
            decimals={0}
            accent="ocean"
          />
        )}
        {strength.ok && strength.total_tonnage != null && (
          <StatTile
            label={t("overview.tonnage")}
            value={strength.total_tonnage}
            decimals={0}
            suffix=" kg"
            accent="violet"
          />
        )}
      </motion.div>

      {/* Body trends */}
      {body.ok && (
        <motion.div {...(fade(0.08) as object)}>
          <Card>
            <CardHeader title={t("body.title")} subtitle={t("body.subtitle")} />
            <div className="space-y-6">
              {(body.series?.weight?.length ?? 0) >= 1 && (
                <TrendBlock
                  title={t("metrics.weight")}
                  delta={weightDelta}
                  suffix=" kg"
                  series={body.series!.weight}
                  accent="coral"
                />
              )}
              {(body.series?.body_fat?.length ?? 0) >= 1 && (
                <TrendBlock
                  title={t("metrics.body_fat")}
                  delta={bfDelta}
                  suffix="%"
                  series={body.series!.body_fat}
                  accent="violet"
                />
              )}
            </div>
          </Card>
        </motion.div>
      )}

      {/* Strength PRs */}
      {strength.ok && (strength.prs?.length ?? 0) > 0 && (
        <motion.div {...(fade(0.1) as object)}>
          <Card>
            <CardHeader title={t("strength.title")} subtitle={t("strength.subtitle")} />
            <ul className="space-y-2">
              {strength.prs!.slice(0, 6).map((pr, i) => (
                <li
                  key={i}
                  className="flex items-center justify-between rounded-md bg-surface-sunken px-4 py-3"
                >
                  <span className="font-display font-bold text-body">
                    {pr.exercise_name || t("strength.unnamed")}
                  </span>
                  <span className="text-sm font-bold text-body-muted">
                    {fmtNum(pr.best_1rm, 1)} kg
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        </motion.div>
      )}

      {/* Goals */}
      {goals.ok && (goals.items?.length ?? 0) > 0 && (
        <motion.div {...(fade(0.12) as object)}>
          <Card>
            <CardHeader title={t("goals.title")} subtitle={t("goals.subtitle")} />
            <ul className="space-y-3">
              {goals.items!.map((g, i) => (
                <li key={i} className="rounded-md bg-surface-sunken px-4 py-3">
                  <div className="flex items-center justify-between">
                    <span className="font-display font-bold text-body">
                      {g.kind || g.metric_key}
                    </span>
                    {g.progress_pct != null && (
                      <Badge tone="ember">{fmtNum(g.progress_pct, 0)}%</Badge>
                    )}
                  </div>
                  {g.progress_pct != null && (
                    <div className="mt-2 h-2 overflow-hidden rounded-pill bg-line">
                      <div
                        className="h-full rounded-pill bg-grad-coral"
                        style={{ width: `${Math.max(0, Math.min(100, g.progress_pct))}%` }}
                      />
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </Card>
        </motion.div>
      )}

      <p className="pt-2 text-center text-xs text-body-faint">
        {tp("app.tagline")}
      </p>
    </div>
  );
}

function TrendBlock({
  title,
  delta,
  suffix,
  series,
  accent,
}: {
  title: string;
  delta?: Delta;
  suffix: string;
  series: SeriesPoint[];
  accent: "coral" | "violet" | "ocean" | "lime";
}) {
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <span className="font-display text-xs font-bold uppercase tracking-kicker text-body-soft">
          {title}
        </span>
        {delta && (
          <span className="text-sm font-bold text-body-muted">
            {fmtSigned(delta.change, 1)}
            {suffix}
          </span>
        )}
      </div>
      <TrendChart
        series={series}
        accent={accent}
        decimals={1}
        suffix={suffix}
        ariaLabel={title}
        height={170}
      />
    </div>
  );
}

function ReportSkeleton() {
  return (
    <div className="space-y-4" aria-hidden>
      <div className="grid grid-cols-2 gap-3">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-24 animate-pulse rounded-lg bg-surface-sunken" />
        ))}
      </div>
      <div className="h-60 animate-pulse rounded-xl bg-surface-sunken" />
    </div>
  );
}
